/**
 * 📷 V2X Three.js 相机控制器 - 你的3D世界观察助手
 *
 * 这个文件就像是一个"3D世界的导游"🎯，它会帮助你用鼠标和键盘
 * 来观察3D世界，就像在玩3D游戏或者使用3D建模软件一样！
 *
 * 🤔 什么是轨道控制器？
 * 想象你在观察一个地球仪：
 * 1. 🖱️ 你可以拖动鼠标让地球仪旋转（旋转）
 * 2. 🔍 你可以靠近或远离地球仪（缩放）
 * 3. 📱 你可以移动整个地球仪的位置（平移）
 *
 * 轨道控制器就是让你的鼠标变成"魔法手"，可以这样操控3D场景！
 *
 * 🎮 支持的操作：
 * - 🖱️ 左键拖拽：旋转视角
 * - 🔍 滚轮：缩放远近
 * - 🖱️ 右键拖拽：平移位置
 * - ⚙️ 自动旋转：让场景自动转圈（像展示台）
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MapControls } from 'three/examples/jsm/controls/MapControls.js';

/**
 * 相机控制模式
 * - `map`: 地图模式，左键平移，右键旋转。适合GIS和大型场景。
 * - `orbit`: 轨道模式，左键旋转，右键平移。适合观察单个模型。
 */
export type CameraControlMode = 'map' | 'orbit';

export interface V2XCameraControlsConfig {
  /** 初始模式，默认为 'map' */
  mode?: CameraControlMode;
}

/**
 * 🎛️ 相机控制器配置接口
 *
 * 这就像是"相机设置菜单"，你可以调整各种参数来定制控制体验：
 * 就像调整手机相机的对焦、曝光、防抖等功能一样！
 */
export interface CameraControlsConfig {
  mode?: CameraControlMode;
  // camera-controls库的其他配置可以根据需要添加
}

/**
 * 📷 相机控制器类 - 你的3D视角管理专家
 *
 * 这个类就像是一个"专业摄影师"🎬，它知道如何操控相机，
 * 让你可以从任何角度观察3D世界，还能平滑地切换视角！
 */
export class V2XCameraControls {
  public currentMode: CameraControlMode;
  private camera: THREE.PerspectiveCamera;
  private domElement: HTMLElement;
  private controls: OrbitControls | MapControls;

  /**
   * 🏗️ 构造函数 - 创建相机控制器
   *
   * 就像买一个新的游戏手柄，你需要：
   * 1. 📷 告诉它要控制哪个相机
   * 2. 🎨 告诉它在哪个画布上监听鼠标
   * 3. ⚙️ 设置你喜欢的控制方式
   *
   * @param camera 要控制的透视相机
   * @param domElement 画布元素
   * @param config 可选的配置参数
   */
  constructor(
    camera: THREE.PerspectiveCamera,
    domElement: HTMLElement,
    config: V2XCameraControlsConfig = {}
  ) {
    this.camera = camera;
    this.domElement = domElement;
    this.currentMode = config.mode || 'map';

    this.controls = this.createControls(this.currentMode);

    console.log(
      `[V2X-Three] V2XCameraControls initialized in '${this.currentMode}' mode.`
    );
  }

  private createControls(mode: CameraControlMode): OrbitControls | MapControls {
    let newControls;
    if (mode === 'map') {
      newControls = new MapControls(this.camera, this.domElement);
      // 地图模式下，通常不希望旋转到地平线以下
      newControls.maxPolarAngle = (Math.PI / 2) * 0.95;
    } else {
      // 'orbit'
      newControls = new OrbitControls(this.camera, this.domElement);
    }

    newControls.enableDamping = true;
    newControls.dampingFactor = 0.1;

    return newControls;
  }

  /**
   * 切换相机控制模式
   * @param mode 目标模式 ('map' | 'orbit')
   */
  public setMode(mode: CameraControlMode): void {
    if (this.currentMode === mode) {
      return;
    }

    // 保存当前目标点
    const target = this.controls.target.clone();

    // 清理旧的控制器
    this.controls.dispose();

    // 创建新的控制器
    this.controls = this.createControls(mode);
    this.currentMode = mode;

    // 恢复目标点
    this.controls.target.copy(target);
    this.controls.update();

    console.log(`[V2XCameraControls] Switched to '${mode}' mode.`);
  }

  /**
   * 在渲染循环中调用，以更新控制器状态（例如，阻尼效果）。
   */
  public update(delta?: number): void {
    this.controls.update(delta);
  }

  /**
   * 设置相机焦点
   * @param x X坐标
   * @param y Y坐标
   * @param z Z坐标
   */
  public setTarget(x: number, y: number, z: number): void {
    this.controls.target.set(x, y, z);
  }

  /**
   * 获取当前相机焦点
   */
  public getTarget(): THREE.Vector3 {
    return this.controls.target;
  }

  /**
   * 释放资源
   */
  public dispose(): void {
    this.controls.dispose();
  }

  /**
   * 获取内部的 three.js 控制器实例
   */
  public get internalControls(): OrbitControls | MapControls {
    return this.controls;
  }
}

/*
 * 🎉 总结：相机控制器为你提供了什么？
 *
 * 1. 🎮 直观的3D导航：
 *    - 鼠标左键拖拽旋转视角
 *    - 滚轮缩放远近距离
 *    - 鼠标右键平移整体位置
 *
 * 2. 🎬 专业的动画效果：
 *    - 平滑的视角切换动画
 *    - 自然的缓动函数
 *    - 可配置的动画时长
 *
 * 3. 🔧 丰富的配置选项：
 *    - 自动旋转展示模式
 *    - 可调节的移动速度
 *    - 安全的距离限制
 *
 * 4. 💾 状态管理功能：
 *    - 保存和恢复相机状态
 *    - 智能对象聚焦
 *    - 一键重置功能
 *
 * 💭 使用建议：
 * 这个控制器适合大多数3D应用场景，特别是：
 * - 产品展示和建筑漫游
 * - 教育演示和数据可视化
 * - 艺术作品和游戏场景
 *
 * 记住在每帧动画中调用update()方法，让控制器能够正常工作！
 */
